import { useAuthContext } from "lib/contexts/AuthProvider";
import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import { usePaymentContext } from "lib/contexts/PaymentProvider";
import { useToken } from "lib/contexts/TokenProvider";
import { putRequest } from "lib/core/apiClient";
import { ShippingHandlesType } from "lib/types/checkout";
import { UpSellProductCardProps, UpSellVariantType } from "lib/types/upsell";
import { appendParamsToItemsUrl, getBillingDetails, getItems } from "lib/utils/checkout";
import { analyticsEvents, analyticsTypes, apiURI, eventTypes } from "lib/utils/constants";
import { isEmptyObj, publishPostMessage } from "lib/utils/helpers";
import { errorToast, successToast } from "lib/utils/toasters";
import { getUpsellUrl } from "lib/utils/upsell";
import { useCallback, useState } from "react";
import { mutate } from "swr";
import useSendAnalyticsEvent from "./useAnalytics";
import { useLocale } from "./useLocale";
import { useMerchantContext } from "lib/contexts/MerchantProvider";
import { usePopupOrchestrator } from "lib/contexts/PopupOrchestratorProvider";

interface UseUpsellValuesProps {
  upsellProduct: Omit<UpSellProductCardProps, "isDiscounted">;
  closeVariantSelector?: () => void;
}

export const useUpsellValues = (props: UseUpsellValuesProps) => {
  const { t } = useLocale();
  const { openPopup } = usePopupOrchestrator();
  const { upsellProduct, closeVariantSelector } = props;
  const [isLoading, setIsLoading] = useState(false);
  const {
    state: { tokenId: floTokenId },
  } = useToken();
  const {
    state: { merchant, checkoutMetadata },
  } = useMerchantContext();
  const {
    state: { checkoutId, checkoutView, billing },
    actions: { setCheckoutModal, setShippingHandles, updateCheckoutBasedOnCheckoutResponse },
  } = useCheckoutContext();
  const {
    state: { isAuthenticated },
  } = useAuthContext();
  const {
    actions: { mutatePayment },
  } = usePaymentContext();
  const { initialCheckoutStep } = checkoutMetadata ?? {};

  const { sendAnalyticsEvent } = useSendAnalyticsEvent();

  //states
  const [itemsData, setItemsData] = useState<Record<string, number>>({});

  // Variables
  const { product, openVariantSelector, position } = upsellProduct;

  // Handlers
  const handleEditProduct = (variantId: string, quantity: number) => {
    const tempData = { ...itemsData };
    if (quantity < 1) {
      delete tempData[variantId];
      setItemsData(tempData);
      return;
    }
    tempData[variantId] = quantity;
    setItemsData(tempData);
  };

  const getCompareAtPrice = (variant: UpSellVariantType, isDiscounted: boolean) => {
    if (!variant) return 0;

    if (isDiscounted) {
      if (merchant?.upsellStrikeThroughConfig?.hideCompareAtPriceOnDiscounted) return 0;
      return merchant?.upsellStrikeThroughConfig?.compareWithOriginalPriceOnDiscounted
        ? variant?.originalPrice ?? variant?.currentPrice
        : variant?.currentPrice;
    }

    return merchant?.upsellStrikeThroughConfig?.showUpsellCompareAtPrice
      ? variant?.originalPrice ?? variant?.currentPrice
      : 0;
  };

  const percentageDiscount = (variant: UpSellVariantType, isDiscounted: boolean) => {
    if (!isDiscounted || !variant?.discountedPrice) return 0;
    if (merchant?.upsellStrikeThroughConfig?.hideCompareAtPriceOnDiscounted) return 0;

    const price = merchant?.upsellStrikeThroughConfig?.compareWithOriginalPriceOnDiscounted
      ? variant.originalPrice || variant.currentPrice
      : variant.currentPrice;

    return Math.round(((Number(price) - Number(variant.discountedPrice)) * 100) / Number(price));
  };

  const handleAddBtnClick = async (isVariant: boolean) => {
    if (isVariant) {
      openVariantSelector?.(product);
      return;
    }

    if (position === "EMPTY_CART") {
      setIsLoading(true);
      publishPostMessage(eventTypes.ADD_TO_CART, {
        item: { id: product?.variants?.[0]?.variantId, quantity: 1 },
      });
      return;
    }

    const payload = {
      items: [
        {
          variant_id: product?.variants?.[0]?.variantId,
          quantity: 1,
          has_components: product?.hasComponents,
          tab_title: upsellProduct?.tabTitle,
        },
      ],
    };

    await handleAPIAddLogic(payload);
  };

  const submitSelection = async ({
    itemsObj,
  }: {
    itemsObj?: Record<string, number>;
  } = {}) => {
    const payloadObj = itemsObj ?? itemsData;
    if (!Boolean(Object.keys(payloadObj)?.length)) {
      closeVariantSelector?.();
      return;
    }

    if (position === "EMPTY_CART") {
      setIsLoading(true);
      publishPostMessage(eventTypes.ADD_TO_CART, {
        item: Object.keys(payloadObj)?.map((key: string, index: number) => {
          return {
            id: key,
            quantity: payloadObj[key],
          };
        }),
      });
      return;
    }

    const payload = {
      items: Object.keys(payloadObj)?.map((key: string) => {
        return {
          variant_id: key,
          quantity: payloadObj[key],
          has_components: product?.hasComponents,
          tab_title: upsellProduct?.tabTitle,
        };
      }),
    };

    await handleAPIAddLogic(payload);
    closeVariantSelector?.();
  };

  const handleAPIAddLogic = async (payload: any) => {
    try {
      const AUTH_UPSELL_PATH = appendParamsToItemsUrl(`/checkout/${checkoutId}/upsell`);
      const PUBLIC_UPSELL_PATH = appendParamsToItemsUrl(`/checkout/${checkoutId}/upsell`);
      setIsLoading(true);
      const url = isAuthenticated ? AUTH_UPSELL_PATH : PUBLIC_UPSELL_PATH;
      const response: any = await putRequest(
        url,
        payload,
        Boolean(isAuthenticated) ? "KRATOS_PRIVATE" : "KRATOS_PUBLIC",
      );
      if (!response) {
        errorToast(t("unable_to_add"));
        return;
      }

      const parsedBilling = getBillingDetails(response.pricing) ?? {
        total_payable: 0,
        sub_total: 0,
        total: 0,
        tax: 0,
        rewards: 0,
        discount: 0,
        shipping: 0,
        cod: 0,
        cod_enabled: false,
        taxes_included: true,
        serviceable: true,
        prepaid_total: 0,
        prepaid_discount: 0,
        original_sub_total: 0,
      };

      const itemsObj =
        isEmptyObj(itemsData) && product.variants?.length
          ? { [product.variants?.[0].variantId]: 1 }
          : itemsData;

      sendAnalyticsEvent({
        eventName: analyticsEvents.UPSELL_PRODUCT_ADDED,
        eventType: "click",
        eventFor: [analyticsTypes.SF_ANALYTICS, analyticsTypes.FACEBOOK_PIXEL],
        context: position === "CART" || position === "EMPTY_CART" ? "cart" : "checkout",
        metaData: {
          upsellData: {
            position: position,
            product_id: product.productId,
            upsell_section: "generic",
            group_ids: product.upsellIds,
            upsold_items: Object.keys(itemsObj)?.map((key: string, index: number) => {
              const selectedVariant = product?.variants?.find(
                (variant: UpSellVariantType) => variant?.variantId === key,
              );
              return {
                product_id: product?.productId,
                variant_id: key,
                quantity: itemsData[key],
                item_total: selectedVariant?.currentPrice,
                product_details: selectedVariant,
                is_discounted: selectedVariant?.isDiscountedUpsell,
                tab_title: upsellProduct?.tabTitle,
              };
            }),
          },
          checkoutItems: getItems(response?.items),
          billing: parsedBilling,
        },
      });

      sendAnalyticsEvent({
        eventName: analyticsEvents.FLO_CART_QUANTITY_UPDATED,
        eventType: "click",
        eventFor: [analyticsTypes.SF_ANALYTICS],
        metaData: {
          upsellData: {
            position: position,
            productId: product.productId,
            UpSellSection: "generic",
            groupIds: product.upsellIds,
            upsoldItems: Object.keys(itemsObj)?.map((key: string, index: number) => {
              const selectedVariant = product?.variants?.find(
                (variant: UpSellVariantType) => variant?.variantId === key,
              );
              return {
                productId: product?.productId,
                variantId: key,
                quantity: itemsData[key],
                itemTotal: selectedVariant?.currentPrice,
              };
            }),
          },
          checkoutItems: getItems(response?.items),
        },
      });

      sendAnalyticsEvent({
        eventName: analyticsEvents.FLO_ADDED_TO_CART_UI,
        eventType: "click",
        eventFor: [analyticsTypes.FACEBOOK_PIXEL],
        metaData: {
          checkoutItems: getItems(response?.items),
          billing: parsedBilling,
        },
      });

      successToast(
        t(
          Boolean(payload?.items?.length) || payload?.items?.[0]?.quantity > 1
            ? "upsell_multiple_product_added"
            : "upsell_product_added",
        ),
      );

      if (billing.shipping !== parsedBilling.shipping && upsellProduct?.position !== "CART") {
        successToast(t("shipping_fee_updated"));
      }
      updateCheckoutBasedOnCheckoutResponse(response, "MAIN", true);

      const shippingHandles = response?.metadata?.available_shipping_handles ?? [];
      const showShippingHandles = response?.metadata?.show_shipping_handle_selector ?? false;
      setShippingHandles(shippingHandles as ShippingHandlesType);
      if (
        shippingHandles.length > 0 &&
        Boolean(showShippingHandles) &&
        checkoutView === "PAYMENTS" &&
        initialCheckoutStep !== "PAYMENTS"
      ) {
        openPopup("SHIPPING_HANDLES", {});
      } else {
        mutatePayment();
        mutate(`/checkout/${checkoutId}/rewards`);
        mutate(getUpsellUrl({ checkoutId, tokenId: floTokenId }));

        const DISCOUNT_PATH = appendParamsToItemsUrl(`/checkout/${checkoutId}/discount`);
        mutate(DISCOUNT_PATH);

        mutate(`UPI_INTENT`);
      }
    } catch (e: any) {
      const errorMsg = e?.response?.data?.error?.message;
      if (e?.response?.data?.error?.code === "GE_003") return;
      errorToast(errorMsg ?? t("coupon_not_found"));
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    itemsData,
    setItemsData,
    getCompareAtPrice,
    percentageDiscount,
    handleAddBtnClick,
    handleEditProduct,
    submitSelection,
  };
};
